/**
 * The dashboard's Ship tab (spec 2026-10-09 §2.2 "2 · Ship", §8 W4 card C2; the career in the Reach only): registers
 * Ship › Status, Loadout, Cargo and Fabrication (`registerDashSection`, `ui/dashboard/tabs/ship`) over a source that
 * reads the Career (the `m2` service), the live player ship off the sim (hull, shield and energy as the HUD reads
 * them), C0's known-station directory and Set course over the Reach, and the `jettison` feature's hook for the
 * Jettison button. Reads happen only while a section is mounted (the dashboard is open), at the shell's refresh rate.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import { HOME_M2_IDS, m2StationId } from '../../bridge/m2/stations'
import { loadJettisonConfig } from '../../career/jettison'
import type { StationId } from '../../data/m2Schemas'
import { stationIndexOf } from '../../data/m2'
import type { ReachRuntime } from '../../reach/runtime/types'
import { createReachStationDirectory, setCourseTo, type StationDirectory } from '../../ui/dashboard/data'
import { registerDashSection } from '../../ui/dashboard/registry'
import type { ShipVitals } from '../../ui/dashboard/tabs/ship/model'
import { shipSections, type ShipTabSource } from '../../ui/dashboard/tabs/ship/sections'
import { Energy, Hull, Life, Pos64, Shield, Ship, Weapons } from '../../world/components'
import { jettisonFromDashboard, jettisonPhase } from '../jettison/feature'

/** After the berth asset list (charter HUD + 4); registration order only places it in the feature list. */
const ORDER = FEATURE_ORDER.charterHud + 6

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

/** The live player ship as the HUD reads it (`hud/hudModel.ts`), or null when there is none. UI rate. */
export function readVitals(ctx: Pick<GameContext, 'sim'>): ShipVitals | null {
  const p = ctx.sim.playerEid
  if (p < 0 || Life.alive[p] !== 1) return null
  const def = ctx.sim.defs[Ship.defIndex[p]]
  if (def === undefined) return null
  return {
    hull: clamp01(Hull.value[p] / def.hull),
    shield: def.shield > 0 ? clamp01(Shield.value[p] / def.shield) : 0,
    energy: clamp01(Energy.value[p] / def.energy),
    torpedoes: Weapons.torpedoes[p],
  }
}

/** The Ship tab's reads and commands over the live game. */
export function shipTabSource(ctx: GameContext, reach: ReachRuntime): ShipTabSource {
  const jcfg = loadJettisonConfig()
  let dir: StationDirectory | null = null
  const directory = (): StationDirectory => {
    dir ??= createReachStationDirectory({
      galaxy: reach.galaxy,
      state: reach.state,
      layoutOf: (id) => reach.layoutOf(id),
      hereSystemId: () => reach.session.layout?.systemId ?? null,
      herePosition: () => {
        const p = ctx.sim.playerEid
        return p < 0 ? null : [Pos64.x[p], Pos64.y[p], Pos64.z[p]]
      },
    })
    return dir
  }
  const m2 = () => ctx.services.maybe('m2')
  const directoryIdOf = (id: StationId): string | null => {
    const d = directory()
    const hit = d.stations.find((s) => m2StationId(s.id) === id)
    if (hit !== undefined) return hit.id
    // The home cluster's sites keep M2's ids (`hub`, `refinery`, `scrapyard`): their site type, not their id.
    if (HOME_M2_IDS.includes(id)) {
      const home = reach.session.layout?.stations.find((s) => s.type === id)
      if (home !== undefined) return home.id
    }
    return null
  }
  return {
    data: () => m2()?.data ?? null,
    career: () => m2()?.ui.getState().career ?? null,
    vitals: () => readVitals(ctx),
    directory,
    setCourse: (stationId) => {
      setCourseTo(reach, directory(), stationId)
    },
    stationName: (id) => {
      const dirId = directoryIdOf(id)
      const known = dirId === null ? undefined : directory().stations.find((s) => s.id === dirId)
      if (known !== undefined) return known.name
      const data = m2()?.data
      const i = data === undefined ? -1 : stationIndexOf(data, id)
      return data !== undefined && i >= 0 ? data.stations.stations[i].name : id
    },
    directoryIdOf,
    jettisonPhase,
    jettison: (share) => {
      jettisonFromDashboard(share)
    },
    jettisonShares: jcfg.shares,
    jettisonAll: jcfg.text.all,
  }
}

export interface ShipTabFeature extends GameFeature {
  /** True once the Ship sections are registered (a career in the Reach). */
  readonly registered: boolean
  dispose(): void
}

export function createShipTabFeature(): ShipTabFeature {
  const offs: (() => void)[] = []
  return {
    name: 'shipTab',
    order: ORDER,
    get registered(): boolean {
      return offs.length > 0
    },
    init(ctx: GameContext): void {
      const reach = ctx.reach
      if (reach === null || reach === undefined || ctx.mode !== 'career') return
      const src = shipTabSource(ctx, reach)
      for (const s of shipSections(src)) offs.push(registerDashSection(s))
    },
    dispose(): void {
      for (const off of offs.splice(0)) off()
    },
  }
}

const feature: ShipTabFeature = createShipTabFeature()

export default feature
